import { getNativeBridgeFunction } from 'libs/shared-ui/src/commonUtils/nativeBridge';
import { ConnectError } from '@bufbuild/connect';
import { getSessionData } from '@events/student/eventsCreator/utils';
import {
  Loader,
  callStudentEventsApi,
  clearLocalStorageKeys,
  deleteOfflineAccessKeyFromCache,
  deserify,
  getDeviceType,
  getLocalStorage,
  setLocalStorage,
  storeOfflineAccessKeyInCache,
  useCommonServiceClientContext,
  useNotificationPermission,
} from '@geneo2-web/shared-ui';
import {
  DeviceEnum,
  ProfileRolesEnum,
} from '@protos/user_management/ums.db_pb';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_STUDENT_DASHBOARD,
  LEARN_DASHBOARD,
  SCREEN_RESOLVER,
} from '../../../routeHandling/RoutesNomenclature';
import {
  resetHomeState,
  setHomeSelectedSubjectId,
} from '../../Home/reducer/homeDashboard.slice';
import { resetHomeworkState } from '../../Homework/reducer/homework.slice';
import {
  aiHomeworkCloseEvent,
  aiHomeworkReviewCloseEvent,
} from '../../Homework/utils_homework/homework.events';
import { resetLearnState } from '../../Learn/reducer/learn.slice';
import {
  aiBookCloseEvent,
  aiChapterCloseEvent,
  aiContentCloseEvent,
  aiLessonCloseEvent,
  aiResourceCloseEvent,
  aiResourcePageCloseEvent,
  aiTopicCloseEvent,
} from '../../Learn/utils_learn/learn.events';
import { resetPerformanceState } from '../../Performance/reducer/performance.slice';
import { aiPerformanceCloseEvent } from '../../Performance/utils_performance/performance.events';
import {
  aiLoginEvent,
  aiLogoutEvent,
  interactionEvent,
} from '../Login/login_logout.events';
import {
  resetAuthState,
  setIsLoggingOut,
  setIsMCBUser,
  setIsSsoUser,
  setSessionMode,
  // setIsMCBUser,
  setSsoRedirectionUrl,
  setSsoSubjectIds,
  setUserInfo,
} from '../reducer/auth.slice';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { MCBErrorScreen } from '../MCBLogin/components/MCBErrorScreen';
import { jwtDecode } from 'jwt-decode';
import { resetMultiSubjectFAState } from '../../Homework/MultiSubjectFormativeAssessment/reducer/multisubjectFA.slice';
import { getSubjectEnumFromStringEnum } from '../../../utils/commonUtils';
import { StudentLoginResponseType } from '@protos/user_management/ums.login.apis_pb';

export const GenericSsoLogin = () => {
  const { external_client } = useParams();
  const location = useLocation();
  const uid =
    new URLSearchParams(location.search).get('uid') ||
    new URLSearchParams(location.search).get('uId') ||
    undefined;
  const token = new URLSearchParams(location.search).get('token') || undefined;
  const mode = new URLSearchParams(location.search).get('mode') || undefined;
  const subjectEnum =
    new URLSearchParams(location.search).get('subject') || undefined;
  // Optional explicit tenant override for multi-tenant SSO providers (see
  // validateUserSsoLoginV4). Most providers don't need this — the backend
  // falls back to the school_group's configured tenantId when omitted.
  const tenantId =
    new URLSearchParams(location.search).get('tenant_id') || undefined;

  const navigate = useNavigate();
  let auth = getLocalStorage('auth');
  if (auth === 'true' || auth === 'false') {
    auth = JSON.parse(auth);
  }
  const commonServiceClientContext = useCommonServiceClientContext();
  const {
    UmsSsoAPIServiceV1ClientWithStatusCodeHandler,
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
  } = commonServiceClientContext;

  useEffect(() => {
    geneoSsoLogin();
  }, [external_client, uid, token]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const dispatch = useDispatch();
  const deviceType = getDeviceType();
  const userInfo = deserify(useAppSelector((state) => state.auth.userInfo));
  const userId = getLocalStorage('userId');
  const fcmToken = getLocalStorage('fcmToken'); // Convert fcmToken to string
  const deviceId = getLocalStorage('deviceID');
  const { selected_book_id } = deserify(useAppSelector((state) => state.home));
  const {
    selected_chapter_resources,
    selected_module_lessons,
    selected_lesson_content,
    selected_resource_content,
    selected_resource_info,
  } = deserify(useAppSelector((state) => state.learn));
  const { isOffline } = useDownloadContext();
  const { performance_id } = deserify(
    useAppSelector((state) => state.performance)
  );

  const { selected_homework_data } = deserify(
    useAppSelector((state) => state.homework)
  );

  const { permissionGranted, requestPermission } = useNotificationPermission(
    'student',
    isOffline
  );

  const notificationAPI = async () => {
    if (!userId) {
      console.log('User ID not found.');
      return;
    }
    if (isOffline) return;

    try {
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceId: deviceId ? BigInt(deviceId) : undefined,
        profileId: BigInt(userId),
        role: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
        deviceType: DeviceEnum.DEVICE_TYPE_WEB,
        deviceToken: fcmToken?.toString(),
        isActive: false,
      });
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceToken: fcmToken?.toString(),
        topic: `${String(userId)}_${ProfileRolesEnum.PROFILE_ROLE_STUDENT}`,
        subscribeTopic: false,
      });
    } catch (err) {
      console.error('Error in Notification API:', err);
    }
  };

  const commonAiCloseEvents = async () => {
    await aiContentCloseEvent({
      pageNumber: selected_resource_info?.pageNumber,
      pageContent: selected_resource_info?.pageContent || [],
      isOffline: isOffline,
    });
    await aiResourcePageCloseEvent({
      resourceId: selected_resource_content?.parsedData.resourceId,
      pageNumber: selected_resource_info?.pageNumber,
      isOffline: isOffline,
    });
    await aiResourceCloseEvent({
      resourceId: selected_resource_content?.parsedData.resourceId,
      isOffline: isOffline,
    });
    await aiLessonCloseEvent({
      lessonId: selected_lesson_content?.lessonId,
      isOffline: isOffline,
    });
    await aiTopicCloseEvent({
      topicId: selected_module_lessons?.moduleId,
      isOffline: isOffline,
    });
    await aiChapterCloseEvent({
      chapterId: selected_chapter_resources?.chapterId,
      isOffline: isOffline,
    });

    await aiPerformanceCloseEvent({
      performanceId: performance_id,
      isOffline: isOffline,
    });

    await aiHomeworkCloseEvent({
      homeworkId: selected_homework_data?.homeworkId,
      isOffline: isOffline,
    });
    await aiHomeworkReviewCloseEvent({
      homeworkId: selected_homework_data?.homeworkId,
      isOffline: isOffline,
    });
    await aiBookCloseEvent({
      bookId: selected_book_id,
      isOffline: isOffline,
    });
    await aiLogoutEvent({ isOffline: isOffline });
    !isOffline && (await callStudentEventsApi(commonServiceClientContext));
  };

  // Function to handle logout and state reset
  const handleLogout = async () => {
    try {
      dispatch(setIsLoggingOut(true));
      await commonAiCloseEvents();
      if (fcmToken) {
        await notificationAPI();
      }
      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.logout({
          profileId: getLocalStorage('userId'),
          role: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
        });
    } catch (err) {
      console.log(err);
      dispatch(setIsLoggingOut(false));
    } finally {
      dispatch(resetAuthState());
      dispatch(resetHomeState());
      dispatch(resetLearnState());
      dispatch(resetPerformanceState());
      dispatch(resetHomeworkState());
      dispatch(resetMultiSubjectFAState());
      // localStorage.clear();
      clearLocalStorageKeys();
      deleteOfflineAccessKeyFromCache();
    }
  };

  function pushUserDetailsToAndroid(uToken: string, userID: string): void {
    try {
      if (getNativeBridgeFunction('pushUserDetails')) {
        getNativeBridgeFunction('pushUserDetails')?.(uToken, userID);
      }
    } catch (error) {
      console.log('Error pushing user details to Android', error);
    }
  }

  const handlePostLoginNavigation = (user_info?: StudentLoginResponseType) => {
    dispatch(setSessionMode((mode as 'learn' | 'assess') || undefined));
    dispatch(setIsSsoUser(true));
    const qrId = getLocalStorage('qrId');
    if (qrId) {
      navigate(`${SCREEN_RESOLVER}?QRId=${qrId}`);
      return;
    }

    if (subjectEnum) {
      const subjectEnums = subjectEnum
        .split(',')
        .map((val) => getSubjectEnumFromStringEnum(val))
        .filter((val) => !!val);
      const selectedSubjects =
        subjectEnums.length > 0
          ? subjectEnums
              .map((val) =>
                user_info?.learnSubjects?.find((sub) => sub.subjectEnum === val)
              )
              .filter((val) => !!val)
          : undefined;
      const subIds = (selectedSubjects?.map((val) => val?.subjectId) ||
        []) as number[];
      dispatch(setSsoSubjectIds(subIds));
      if (subIds.length !== 1) {
        navigate(HOME, {
          state: {
            from: location.pathname,
          },
        });
      } else {
        const subId = subIds[0];
        dispatch(setHomeSelectedSubjectId(subId));
        if (mode === 'learn') {
          navigate(`${LEARN_DASHBOARD}/${subId}`);
        } else if (mode === 'assess') {
          navigate(HOMEWORK_STUDENT_DASHBOARD);
        }
      }
      return;
    } else {
      dispatch(setSsoSubjectIds([]));
    }

    navigate(HOME);
  };

  const geneoSsoLogin = async () => {
    try {
      if (
        !external_client ||
        !['edunura', 'schoolnet-cl', 'vk12'].includes(external_client)
      ) {
        setErrorMessage('Invalid sso client');
        return;
      }
      const clientDetails = {
        edunura: {
          origin: 'EDUNURA',
          organizationCode: 'EDUNURA-001',
        },
        'schoolnet-cl': {
          origin: 'SCHOOLNET-CL',
          organizationCode: 'SCHOOLNET-CL-001',
        },
        // TEMPORARY: mock provider for VK12's incoming ERP partner (MCB
        // replacement) — swap origin/organizationCode for the real vendor
        // once confirmed. See sso-vendor-integration-guide-template.md.
        vk12: {
          origin: 'SCHOOLCANVAS',
          organizationCode: '1300',
        },
      } as const;
      type ExternalClientKey = keyof typeof clientDetails;
      const clientKey = external_client as ExternalClientKey;
      const selectedClient = clientDetails[clientKey];

      if (uid && token) {
        const res =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.validateUserSsoLoginV4(
            {
              role: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
              requestOrigin: selectedClient.origin,
              token,
              uniqueId: uid,
              organizationCode: selectedClient.organizationCode,
              tenantId,
            }
          );
        const userId = res.userInfo?.profileId;
        const ssoRedirectionUrl = res.ssoRedirectionUrl;

        const resSession =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.createSsoLoginSession(
            {
              profileId: userId,
            }
          );
        const response =
          await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchStudentProfile(
            {
              studentProfileId: userId,
            }
          );
        const user_info = response.data;

        if (auth && userId) {
          if (userId !== userInfo?.studentProfileId) {
            await handleLogout();
          } else {
            handlePostLoginNavigation(user_info);
            return;
          }
        }

        if (ssoRedirectionUrl) {
          dispatch(setSsoRedirectionUrl(ssoRedirectionUrl));
        }
        if (resSession.activeSessionId) {
          localStorage.setItem(
            'activeSession',
            resSession.activeSessionId.toString()
          );
        }

        if (!user_info) {
          console.log('User info not present');
          return;
        }
        dispatch(setUserInfo(user_info));
        // dispatch(setIsMCBUser(true));
        dispatch(setIsMCBUser(true));
        const accessKey = user_info.studentProfileId
          ? 'student-' + user_info.studentProfileId.toString()
          : 'geneo';
        await storeOfflineAccessKeyInCache(accessKey);
        setLocalStorage('userId', user_info.studentProfileId.toString());
        setLocalStorage('role', 'student');
        setLocalStorage('auth', 'true');
        setTimeout(async () => {
          await interactionEvent({
            url: 'student_login',
            context: 'sso_login',
            name: 'Login',
            isOffline: isOffline,
          });
        }, 3000);
        await aiLoginEvent({
          studentId: Number(user_info.studentProfileId),
          sessionId: resSession.activeSessionId.toString(),
          boardId: user_info.boardMediumDetails?.boardId,
          classId: user_info.classSectionDetails?.classId,
          mediumId: user_info.boardMediumDetails?.mediumId,
          regionId: user_info.regionId,
          schoolId: user_info.schoolDetails?.schoolId,
          sectionId: user_info.classSectionDetails?.sectionId,
          isOffline: isOffline,
        });
        pushUserDetailsToAndroid(
          user_info.token,
          user_info.studentProfileId.toString()
        );

        handlePostLoginNavigation(user_info);
        if (deviceType !== 'android') {
          await requestPermission();
        }
      }
    } catch (err: any) {
      if (err instanceof ConnectError) {
        setErrorMessage(err.message.replace(/.*\]\s*/, '').trim());
        return;
      }
      setErrorMessage('Internal Error');
    }
  };

  // ----------------------------------------------------

  if (errorMessage || !(uid && token)) {
    return (
      <MCBErrorScreen
        errorMessage={errorMessage || 'Invalid unique id or token'}
        isCustomError={true}
      />
    );
  }
  return <Loader />;
};
